import { SANDBOX_VALIDATION_PRODUCT_ID, type StoreEnvironment } from './catalog';

/**
 * The one scripted scenario a sandbox charge may ask the provider for.
 *
 * ## What this is for
 *
 * Mercado Pago's Orders test procedure approves a sandbox Pix automatically
 * when the payer carries prescribed values. That is genuinely useful - it is
 * how the whole path (charge, notification, authoritative read, verification,
 * grant) can be proved without anybody scanning a code with a real bank app.
 *
 * ## Why it is a server decision and nothing else
 *
 * A scenario chosen by the CLIENT is a client that can ask to be approved. It
 * would not matter that the sandbox is fake: the shape - "the browser tells the
 * server how a payment should turn out" - is the shape that gets copied into
 * production the day somebody adds a second provider. So the browser sends a
 * `productId` and an idempotency key, exactly as before, and this function is
 * the ONLY thing that can turn a charge into a scripted one.
 *
 * ## Three conditions, all required
 *
 * The product must be the internal validation product, the API must be running
 * somewhere nothing is real, and the provider must be the sandbox one. Any of
 * the three missing gives `null`, which is an ordinary charge with the account's
 * own e-mail. There is no query parameter, header, cookie, flag or debug URL
 * that reaches this - it takes values the server already holds.
 */
export type StoreSandboxScenario = 'approved_pix';

/** Which world the provider's credentials belong to. Declared, never inferred. */
export type StoreProviderEnvironment = 'sandbox' | 'production';

/** Where a scripted scenario may exist at all. Production is not in this list. */
const SCENARIO_ENVIRONMENTS: readonly StoreEnvironment[] = Object.freeze([
  'development',
  'test',
] as const);

/**
 * May the Pix validation product be OFFERED at all right now?
 *
 * Stricter than the catalogue's own environment lock, and deliberately so. That
 * lock answers "may this exist here"; this answers "is there any point". The
 * product's only purpose is to drive a real sandbox charge end to end, so a
 * machine with no credentials, or with the shop shut, has nothing to gain from
 * a card that cannot be bought - and a card that cannot be bought is a support
 * question waiting to happen.
 *
 * All four, and it is used in BOTH places that matter: the catalogue that lists
 * it and the order that buys it. Listing it without gating the order would make
 * the gate cosmetic - a forged request does not read the catalogue.
 */
export function sandboxValidationAvailable(input: {
  readonly environment: StoreEnvironment;
  /** The provider's world, or null when no provider is configured at all. */
  readonly providerEnvironment: StoreProviderEnvironment | null;
  readonly purchasingEnabled: boolean;
}): boolean {
  return (
    SCENARIO_ENVIRONMENTS.includes(input.environment) &&
    input.providerEnvironment === 'sandbox' &&
    input.purchasingEnabled
  );
}

export function sandboxScenarioFor(input: {
  readonly productId: string;
  readonly environment: StoreEnvironment;
  readonly providerEnvironment: StoreProviderEnvironment;
}): StoreSandboxScenario | null {
  if (input.productId !== SANDBOX_VALIDATION_PRODUCT_ID) {
    return null;
  }
  if (!SCENARIO_ENVIRONMENTS.includes(input.environment)) {
    return null;
  }
  if (input.providerEnvironment !== 'sandbox') {
    return null;
  }
  return 'approved_pix';
}
